import React, { useEffect, useMemo, useState } from 'react'
import Axios from 'axios'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Card from 'components_new/atoms/Card'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'

import { useTranslation } from 'language/translator'
import { useCustomer } from 'redux/hooks/Customer'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { isMcpEnabledForCustomer } from 'helpers/Functions/isMcpEnabled'

const API_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

interface OAuthParams {
  responseType: string
  clientId: string
  redirectUri: string
  codeChallenge: string
  codeChallengeMethod: string
  state: string | null
  scope: string
}

interface ClientInfo {
  client_id: string
  client_name: string
  vendor: string

  // Whether the redirect_uri in the URL is registered for the client.
  redirect_uri_valid: boolean
}

function parseParamsFromUrl(): OAuthParams | null {
  const q = new URLSearchParams(window.location.search)
  const clientId = q.get('client_id') || ''
  const redirectUri = q.get('redirect_uri') || ''
  const codeChallenge = q.get('code_challenge') || ''
  const codeChallengeMethod = q.get('code_challenge_method') || ''

  // PKCE is optional: public clients (Claude, ChatGPT) send a code_challenge,
  // but confidential clients that authenticate with a client_secret (M365
  // federated connectors / Copilot Studio via teams.microsoft.com) omit it.
  // The backend /authorize + /token accept this (client_secret carries the
  // exchange), so the consent page must not reject it either.
  if (!clientId || !redirectUri) {
    return null
  }

  return {
    responseType: q.get('response_type') || 'code',
    clientId,
    redirectUri,
    codeChallenge,
    codeChallengeMethod,
    state: q.get('state'),
    scope: q.get('scope') || 'openid email profile'
  }
}

// Only https URLs, or http on localhost, are navigated to.
function isNavigableUrl(uri: string): boolean {
  try {
    const url = new URL(uri)

    return (
      url.protocol === 'https:' ||
      (url.protocol === 'http:' &&
        ['localhost', '127.0.0.1'].includes(url.hostname))
    )
  } catch {
    return false
  }
}

// Sends the user back to the client with access_denied, but only to a
// redirect_uri the backend confirmed is registered for the client; any
// other request goes to the portal start page instead.
function cancelRedirect(params: OAuthParams, client: ClientInfo | null) {
  if (!client?.redirect_uri_valid || !isNavigableUrl(params.redirectUri)) {
    window.location.replace('/')

    return
  }

  const url = new URL(params.redirectUri)

  url.searchParams.set('error', 'access_denied')
  url.searchParams.set(
    'error_description',
    'User denied the authorization request'
  )
  if (params.state) url.searchParams.set('state', params.state)
  window.location.replace(url.toString())
}

const McpConsent = () => {
  const { t } = useTranslation()
  const params = useMemo(parseParamsFromUrl, [])
  const { account } = useAccountSettings()
  const customer = useCustomer()
  const mcpEnabled = isMcpEnabledForCustomer(customer)
  const [client, setClient] = useState<ClientInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const accessLines = [
    t('mcp_consent', 'access_metrics'),
    t('mcp_consent', 'access_entities'),
    t('mcp_consent', 'access_reports')
  ]

  useEffect(() => {
    if (!params) return

    Axios.get<ClientInfo>(`${API_URL}/authorize/client-info`, {
      params: {
        client_id: params.clientId,
        redirect_uri: params.redirectUri
      },
      withCredentials: true
    })
      .then((res) => setClient(res.data))
      .catch(() => setError(t('mcp_consent', 'error_load_client')))
  }, [params])

  const handleApprove = async () => {
    if (!params) return
    setSubmitting(true)
    setError(null)

    try {
      const res = await Axios.post<{ redirect_url: string }>(
        `${API_URL}/authorize/consent`,
        {
          response_type: params.responseType,
          client_id: params.clientId,
          redirect_uri: params.redirectUri,
          code_challenge: params.codeChallenge,
          code_challenge_method: params.codeChallengeMethod,
          scope: params.scope,
          state: params.state
        },
        { withCredentials: true }
      )

      window.location.replace(res.data.redirect_url)
    } catch (e) {
      setSubmitting(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail =
        (e as any)?.response?.data?.error_description ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e as any)?.message ||
        t('mcp_consent', 'error_generic')

      setError(detail)
    }
  }

  const handleCancel = () => {
    if (params) cancelRedirect(params, client)
    else window.location.replace('/')
  }

  const invalidRedirect = client !== null && !client.redirect_uri_valid

  if (!params) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error" variant="outlined">
          {t('mcp_consent', 'error_missing_params')}
        </Alert>
      </Box>
    )
  }

  if (!mcpEnabled) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="warning" variant="outlined">
          {t('mcp_consent', 'not_available')}
        </Alert>
      </Box>
    )
  }

  const subtitle = client
    ? t('mcp_consent', 'subtitle_with_client', false, {
      client: client.client_name
    })
    : t('mcp_consent', 'subtitle_generic')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        minHeight: '100vh',
        py: 8,
        px: 2,
        bgcolor: 'background.default'
      }}
    >
      <Card sx={{ maxWidth: 560, width: '100%', p: 4 }}>
        <Stack direction="column" spacing={3}>
          <Box>
            <Text variant="h4">{t('mcp_consent', 'title')}</Text>
            <Text color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              {subtitle}
            </Text>
          </Box>

          <Box>
            <Text variant="subtitle1" sx={{ mb: 1 }}>
              {t('mcp_consent', 'access_heading')}
            </Text>
            <Stack direction="column" spacing={1}>
              {accessLines.map((line) => (
                <Text key={line} variant="body2">
                  • {line}
                </Text>
              ))}
            </Stack>
          </Box>

          {invalidRedirect ? (
            <Alert severity="error" variant="outlined">
              {t('mcp_consent', 'error_invalid_redirect')}
            </Alert>
          ) : null}

          {client && client.vendor !== 'customer' ? (
            <Alert severity="warning" variant="outlined">
              {t('mcp_consent', 'platform_warning', false, {
                client: client.client_name
              })}
            </Alert>
          ) : null}

          {account?.email ? (
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2
              }}
            >
              <Text variant="caption" color="text.secondary">
                {t('mcp_consent', 'logged_in_as')}
              </Text>
              <Text variant="body2">{account.email}</Text>
              {customer?.name ? (
                <>
                  <Text
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {t('mcp_consent', 'customer')}
                  </Text>
                  <Text variant="body2">{customer.name}</Text>
                </>
              ) : null}
            </Box>
          ) : null}

          {error ? (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          ) : null}

          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: 'flex-end' }}
          >
            <Button variant="text" onClick={handleCancel} disabled={submitting}>
              {t('mcp_consent', 'cancel')}
            </Button>
            <Button
              color="brand"
              disabled={client === null || invalidRedirect}
              onClick={handleApprove}
              loading={submitting}
              loadingTitle={t('mcp_consent', 'approving')}
            >
              {t('mcp_consent', 'approve')}
            </Button>
          </Stack>

          <Text variant="caption" color="text.secondary">
            {t('mcp_consent', 'revoke_hint')}
          </Text>
        </Stack>
      </Card>
    </Box>
  )
}

export default McpConsent
